import * as ImmutableLib from "immutable";
import { useQuery } from "@tanstack/react-query";
import fetch from "logic/rest/FetchProvider";
import ApiRoutes from "routing/ApiRoutes";
import { qualifyUrl } from "util/URLUtils";
import PaginationURL from "util/PaginationURL";
import Role from "logic/roles/Role";
import UserOverview from "logic/users/UserOverview";
export const AUTHZ_ROLES_QUERY_KEY = ["authz", "roles"];
const _responseToPaginatedList = ({
  count,
  total,
  page,
  per_page,
  query,
  roles = [],
  context = { users: void 0 }
}) => ({
  list: ImmutableLib.List(roles.map((r) => Role.fromJSON(r))),
  pagination: {
    query,
    page,
    perPage: per_page,
    count,
    total
  },
  context
});
const _responseToPaginatedUserList = ({
  count,
  total,
  page,
  per_page,
  query,
  users
}) => ({
  list: ImmutableLib.List(users.map((u) => UserOverview.fromJSON(u))),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  }
});
const encodeApiUrl = (apiRoute, uriParams = []) => {
  const encodedUriParams = uriParams.map((param) => encodeURIComponent(param));
  return apiRoute(...encodedUriParams).url;
};
export const loadRole = (roleId) => {
  const url = qualifyUrl(encodeApiUrl(ApiRoutes.AuthzRolesController.load, [roleId]));
  return fetch("GET", url).then(Role.fromJSON);
};
export const deleteRole = (roleId) => {
  const url = qualifyUrl(encodeApiUrl(ApiRoutes.AuthzRolesController.delete, [roleId]));
  return fetch("DELETE", url);
};
export const addMembersToRole = (roleId, usernames) => {
  const url = encodeApiUrl(ApiRoutes.AuthzRolesController.addMembers, [roleId]);
  return fetch("PUT", qualifyUrl(url), usernames.toArray());
};
export const removeMemberFromRole = (roleId, username) => {
  const url = encodeApiUrl(ApiRoutes.AuthzRolesController.removeMember, [roleId, username]);
  return fetch("DELETE", qualifyUrl(url));
};
export const loadUsersForRole = (roleId, { page, perPage, query }) => {
  const apiUrl = encodeApiUrl(ApiRoutes.AuthzRolesController.loadUsersForRole, [roleId]);
  const url = PaginationURL(apiUrl, page, perPage, query);
  return fetch("GET", qualifyUrl(url)).then(_responseToPaginatedUserList);
};
export const loadRolesForUser = (username, { page, perPage, query }) => {
  const apiUrl = encodeApiUrl(ApiRoutes.AuthzRolesController.loadRolesForUser, [username]);
  const url = PaginationURL(apiUrl, page, perPage, query);
  return fetch("GET", qualifyUrl(url)).then(_responseToPaginatedList);
};
export const loadRolesPaginated = ({ page, perPage, query }) => {
  const apiUrl = encodeApiUrl(ApiRoutes.AuthzRolesController.list);
  const url = PaginationURL(apiUrl, page, perPage, query);
  return fetch("GET", qualifyUrl(url)).then(_responseToPaginatedList);
};
export const useAuthzRolesPaginated = (pagination) => useQuery({
  queryKey: [...AUTHZ_ROLES_QUERY_KEY, "paginated", pagination],
  queryFn: () => loadRolesPaginated(pagination)
});
export const useRolesForUser = (username, pagination) => useQuery({
  queryKey: [...AUTHZ_ROLES_QUERY_KEY, "for-user", username, pagination],
  queryFn: () => loadRolesForUser(username, pagination),
  enabled: !!username
});
export const useUsersForRole = (roleId, pagination) => useQuery({
  queryKey: [...AUTHZ_ROLES_QUERY_KEY, "users-for-role", roleId, pagination],
  queryFn: () => loadUsersForRole(roleId, pagination),
  enabled: !!roleId
});
